import { useId } from 'react';

/*
 * Garden Bloom icons for the Home welcome (HPP-1). Inline SVG, theme colours
 * only (src/index.css: bg #1a1816, gold #c4a265, cream #e8d4a0, text #ede6db,
 * sage #7baf7b, plus tints and shades of them), no raster images. Decorative:
 * the owning button carries the accessible name, so both are aria-hidden.
 *
 * Animated parts are separate groups that only change transform or opacity
 * (CSS filters on SVG child elements are unreliable in WebKit):
 *   OliveBranchIcon: .leaves (lift) and .sheen (silver-gold shimmer).
 *   MustardSproutIcon: .unfurl (a second small leaf) and .unfurlGlow.
 * The classes come from the caller so the motion lives in its CSS module.
 */

type Classes = Partial<Record<'leaves' | 'sheen' | 'unfurl' | 'unfurlGlow', string>>;

/** A narrow lance-shaped olive leaf along +x from its base at the origin. */
function lanceLeaf(length: number, half: number): string {
  const l = length;
  return `M0 0 C${(l * 0.24).toFixed(2)} ${(-half * 1.1).toFixed(2)} ${(l * 0.62).toFixed(2)} ${(-half).toFixed(2)} ${l} 0 `
    + `C${(l * 0.62).toFixed(2)} ${half.toFixed(2)} ${(l * 0.24).toFixed(2)} ${(half * 1.1).toFixed(2)} 0 0Z`;
}
function upperEdge(length: number, half: number): string {
  const l = length;
  return `M0 0 C${(l * 0.24).toFixed(2)} ${(-half * 1.1).toFixed(2)} ${(l * 0.62).toFixed(2)} ${(-half).toFixed(2)} ${l} 0`;
}

/* Opposite pairs along the twig's curve, smaller toward the tip, plus the
   terminal leaf (positions computed from the twig's cubic Bezier). */
const OLIVE_LEAVES = [
  { x: 36.30, y: 66.33, rot: -117.4, len: 28 },
  { x: 36.30, y: 66.33, rot: -9.4, len: 28 },
  { x: 43.36, y: 54.01, rot: -107.3, len: 27 },
  { x: 43.36, y: 54.01, rot: -7.3, len: 27 },
  { x: 51.84, y: 41.90, rot: -99.9, len: 24 },
  { x: 51.84, y: 41.90, rot: -5.9, len: 24 },
  { x: 60.90, y: 30.59, rot: -94.9, len: 19 },
  { x: 60.90, y: 30.59, rot: -4.9, len: 19 },
  { x: 72.00, y: 18.00, rot: -47.5, len: 16 },
] as const;

const safeId = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, '');

/** Praise: an olive branch (Psalm 52:8-9), gold-lit, on a small soil mound. */
export function OliveBranchIcon({ className, classes = {} }: { className?: string; classes?: Classes }) {
  const id = safeId(useId());
  const leafShapes = OLIVE_LEAVES.map((leaf) => ({ ...leaf, d: lanceLeaf(leaf.len, leaf.len * 0.145), edge: upperEdge(leaf.len, leaf.len * 0.145) }));
  return (
    <svg className={className} viewBox="0 0 100 100" aria-hidden="true" focusable="false" shapeRendering="geometricPrecision">
      <defs>
        <radialGradient id={`${id}-glow`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#c4a265" stopOpacity="0.26" />
          <stop offset="1" stopColor="#c4a265" stopOpacity="0" />
        </radialGradient>
        {/* Sage upper face, silvery underside across the leaf's width. */}
        <linearGradient id={`${id}-leaf`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#5e8f5e" />
          <stop offset="0.48" stopColor="#7baf7b" />
          <stop offset="0.53" stopColor="#b7c4ab" />
          <stop offset="1" stopColor="#dde3d4" />
        </linearGradient>
        <radialGradient id={`${id}-olive`} cx="0.36" cy="0.3" r="0.78">
          <stop offset="0" stopColor="#bcd8a0" />
          <stop offset="0.45" stopColor="#7baf7b" />
          <stop offset="1" stopColor="#4a724b" />
        </radialGradient>
        <linearGradient id={`${id}-soil`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#4a3b2c" />
          <stop offset="1" stopColor="#211a15" />
        </linearGradient>
      </defs>
      <circle data-glow="" cx="50" cy="50" r="42" fill={`url(#${id}-glow)`} />
      {/* Recentred on its own enclosing circle and scaled so every part stays
          within 44 units of the centre: at 88% of the glass circle that is
          more than 10% of the diameter clear of the rim at every size. */}
      <g transform="translate(50 50) scale(0.85) translate(-56 -47)">
      <g transform="translate(5 0)">
      <path d="M17 86 C30 77 70 77 83 86 C68 91 32 91 17 86Z" fill={`url(#${id}-soil)`} />
      <path d="M26 85.5 h0.01 M40 82 h0.01 M60 83 h0.01 M71 85.8 h0.01" stroke="#c4a265" strokeOpacity="0.35" strokeWidth="1.1" strokeLinecap="round" />
      {/* A slender pale woody twig, rising diagonally to the right. */}
      <path d="M30 82 C36 62 50 42 72 18" fill="none" stroke="#a88d63" strokeWidth="2.6" strokeLinecap="round" />
      <path d="M30 82 C36 62 50 42 72 18" fill="none" stroke="#e6d6b4" strokeWidth="1.1" strokeLinecap="round" transform="translate(-0.5 -0.3)" />
      {/* Two smooth oval green olives hanging on short stems below the
          lowest leaves, clear of the twig and the soil. */}
      <path d="M34.6 70 Q30.8 66.4 28.6 63.6 M34.4 70.8 Q27.6 69.2 23.2 68.4" fill="none" stroke="#bda57a" strokeWidth="0.9" strokeLinecap="round" />
      <ellipse cx="26.6" cy="68.8" rx="4.9" ry="6.3" transform="rotate(24 26.6 68.8)" fill={`url(#${id}-olive)`} />
      <ellipse cx="19.8" cy="74" rx="4.5" ry="5.8" transform="rotate(36 19.8 74)" fill={`url(#${id}-olive)`} />
      <ellipse cx="25.1" cy="66.6" rx="1.3" ry="1.8" transform="rotate(24 25.1 66.6)" fill="#ede6db" opacity="0.45" />
      <ellipse cx="18.5" cy="72" rx="1.2" ry="1.7" transform="rotate(36 18.5 72)" fill="#ede6db" opacity="0.4" />
      <g className={classes.leaves}>
        {leafShapes.map((leaf, index) => (
          <g key={index} transform={`translate(${leaf.x} ${leaf.y}) rotate(${leaf.rot})`}>
            <path d={leaf.d} fill={`url(#${id}-leaf)`} />
            <path d={`M1 0 H${leaf.len - 1.5}`} stroke="#e8d4a0" strokeOpacity="0.45" strokeWidth="0.45" />
            <path d={leaf.edge} fill="none" stroke="#e8d4a0" strokeOpacity="0.5" strokeWidth="0.5" />
          </g>
        ))}
        {/* Silver-gold shimmer: the same leaves in light, shown only on a tap. */}
        <g className={classes.sheen} opacity="0">
          {leafShapes.map((leaf, index) => (
            <path key={index} d={leaf.d} transform={`translate(${leaf.x} ${leaf.y}) rotate(${leaf.rot})`} fill="#f3ead2" />
          ))}
        </g>
      </g>
      </g>
      </g>
    </svg>
  );
}

/** Pray: a tiny round mustard seed sprouting (Matthew 17:20; Mark 9:29). */
export function MustardSproutIcon({ className, classes = {} }: { className?: string; classes?: Classes }) {
  const id = safeId(useId());
  return (
    <svg className={className} viewBox="0 0 100 100" aria-hidden="true" focusable="false" shapeRendering="geometricPrecision">
      <defs>
        <radialGradient id={`${id}-glow`} cx="0.52" cy="0.48" r="0.5">
          <stop offset="0" stopColor="#c4a265" stopOpacity="0.22" />
          <stop offset="1" stopColor="#c4a265" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}-seed`} cx="0.36" cy="0.32" r="0.72">
          <stop offset="0" stopColor="#f2e1b0" />
          <stop offset="0.55" stopColor="#c4a265" />
          <stop offset="1" stopColor="#8e6f3e" />
        </radialGradient>
        <linearGradient id={`${id}-leaf`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#9cc89c" />
          <stop offset="1" stopColor="#5f935f" />
        </linearGradient>
        <linearGradient id={`${id}-soil`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#4a3b2c" />
          <stop offset="1" stopColor="#211a15" />
        </linearGradient>
        <radialGradient id={`${id}-bud`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#e8d4a0" stopOpacity="0.55" />
          <stop offset="1" stopColor="#e8d4a0" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle data-glow="" cx="50" cy="50" r="36" fill={`url(#${id}-glow)`} />
      {/* Recentred and scaled like the olive branch (enclosing radius 44). */}
      <g transform="translate(50 50) scale(0.99) translate(-51 -47.5)">
      <path d="M22 81 C34 72 66 72 78 81 C64 86.5 36 86.5 22 81Z" fill={`url(#${id}-soil)`} />
      <path d="M31 81.5 h0.01 M64 80 h0.01 M71 82.6 h0.01" stroke="#c4a265" strokeOpacity="0.35" strokeWidth="1.1" strokeLinecap="round" />
      {/* A slender pale sprout rising from the seed. */}
      <path d="M50 71.4 C50.8 61 46.4 52 49 43 C50.6 37.2 54.2 32.6 58.6 29" fill="none" stroke="#e9dcbf" strokeWidth="2.4" strokeLinecap="round" />
      {/* The tiny, perfectly round seed, golden-tan. */}
      <circle cx="50" cy="75.4" r="4" fill={`url(#${id}-seed)`} />
      <circle cx="48.8" cy="74.1" r="1.2" fill="#fff4dc" opacity="0.7" />
      {/* One sage-green leaf at the tip. */}
      <g transform="translate(58.6 29) rotate(-36)">
        <path d={lanceLeaf(26, 7.4)} fill={`url(#${id}-leaf)`} />
        <path d="M1.2 0 H23.6" stroke="#e8d4a0" strokeOpacity="0.5" strokeWidth="0.6" />
      </g>
      {/* A second small leaf, unfurled only by a tap (hidden at rest). */}
      <g transform="translate(48.6 47) rotate(-152)">
        <circle data-glow="" className={classes.unfurlGlow} cx="8" cy="0" r="12" fill={`url(#${id}-bud)`} opacity="0" />
        <g className={classes.unfurl} opacity="0">
          <path d={lanceLeaf(16, 4.8)} fill={`url(#${id}-leaf)`} />
          <path d="M0.8 0 H14.2" stroke="#e8d4a0" strokeOpacity="0.5" strokeWidth="0.5" />
        </g>
      </g>
      </g>
    </svg>
  );
}
